import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { postRequest, getRequest } from '../../services/apiService';
import { toast } from 'react-toastify';
import ReusableForm from '../../common/ReusableForm';

const UploadTaskForm = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('Download Template');
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    project: null,
    ship: null,
    file: null
  });

  useEffect(() => {
    if (isOpen) {
      setActiveTab('Download Template');
      setFormData({ project: null, ship: null, file: null });
      setIsSubmitting(false);
      setShips([]);
      fetchProjects();
    }
  }, [isOpen]);

  const fetchProjects = async () => {
    try {
      const response = await getRequest('master/projects/');
      const projectList = response?.data || response;
      const parentProjects = projectList.filter(project => !project.parent);
      setProjects(parentProjects.map(p => ({ label: p.name, value: p })));
    } catch (error) {
      toast.error('Failed to fetch projects');
    }
  };

  const fetchShips = async (projectId) => {
    try {
      const response = await getRequest(`master/projects/?parent_id=${projectId}`);
      const shipsList = response?.data || response;
      setShips(shipsList.map(s => ({ label: s.name, value: s })));
    } catch (error) {
      toast.error('Failed to fetch ships');
    }
  };

  const handleProjectChange = (e) => {
    setFormData(prev => ({ ...prev, project: e.value, ship: null }));
    if (e.value) fetchShips(e.value.value.id);
    else setShips([]);
  };

  const handleShipChange = (e) => {
    setFormData(prev => ({ ...prev, ship: e.value }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, file: e.target.files[0] }));
  };

  const handleSubmit = async () => {
    if (!formData.project || !formData.ship) {
      toast.error('Please select both project and ship');
      return;
    }
    if (activeTab === 'Download Template') {
      try {
        setIsSubmitting(true);
        const response = await postRequest('master/sample-csv-download/', {});
        if (response.data?.file_url) {
          const link = document.createElement('a');
          link.href = response.data.file_url;
          link.download = `PERT_Template_${formData.project.label}_${formData.ship.label}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success('PERT template downloaded successfully');
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to download template');
      } finally {
        setIsSubmitting(false);
      }
    } else if (activeTab === 'Upload Data') {
      if (!formData.file) {
        toast.error('Please select a file to upload');
        return;
      }
      try {
        setIsSubmitting(true);
        const uploadForm = new FormData();
        uploadForm.append('file', formData.file);
        uploadForm.append('project_id', formData.project.value.id);
        const endpoint = `/transaction/import-pert-data/${formData.ship.value.id}/`;
        const response = await postRequest(endpoint, uploadForm, true);
        const data = response.data;
        if (data.status === 'success') {
          toast.success(data.message || 'PERT data imported successfully');
          onClose();
        } else if (data.status === 'partial_success') {
          toast.warning(data.message || 'Import completed with some errors');
          if (data.error_file_path) {
            const link = document.createElement('a');
            link.href = `${process.env.REACT_APP_API_BASE_URL}${data.error_file_path}`;
            link.download = data.error_file_path.split('/').pop();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          onClose();
        } else {
          toast.error(data.message || 'Failed to import PERT data');
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error uploading PERT data');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const formFields = [
    {
      name: 'project',
      label: 'Project',
      type: 'select',
      options: projects,
      required: true,
      onChange: handleProjectChange
    },
    {
      name: 'ship',
      label: 'Ship',
      type: 'select',
      options: ships,
      required: true,
      disabled: !formData.project,
      onChange: handleShipChange
    },
    ...(activeTab === 'Upload Data' ? [
      {
        name: 'file',
        label: 'PERT CSV File',
        type: 'file',
        required: true,
        accept: '.csv',
        onChange: handleFileChange
      }
    ] : [])
  ];

  return (
    <Dialog
      visible={isOpen}
      header={
        <div className="flex flex-col">
          <div className="bg-[#1e3a8a] text-white px-6 py-4 rounded-t-lg">
            <span className="text-lg font-semibold">PERT Data Management</span>
          </div>
          <div className="border-b border-gray-200 bg-white">
            <nav className="-mb-px flex">
              {['Download Template', 'Upload Data'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium focus:outline-none ${
                    activeTab === tab
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>
        </div>
      }
      headerClassName="p-0"
      className="max-w-lg w-full mx-auto"
      onHide={onClose}
      modal
      contentClassName="p-0"
    >
      <ReusableForm
        formData={formData}
        setFormData={setFormData}
        fields={formFields}
        onSubmit={handleSubmit}
        loading={isSubmitting}
        submitLabel={activeTab === 'Download Template' ? 'Download Template' : 'Upload Data'}
        hideCancelButton={false}
        onHide={onClose}
      />
    </Dialog>
  );
};

export default UploadTaskForm;